// src/components/Hero.jsx

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Hero = ({ language, content }) => {
  const t = content[language];
  const navigate = useNavigate(); // ✅ Use the hook, not the component
  const location = useLocation();

  // Smooth scroll function
  const handleScrollTo = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }; 
  
  // ✅ Fixed navigation function
  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact'); // ✅ Use navigate() function, not Navigate component
    window.scrollTo(0, 0);
  };

  return (
    <>
      <style>{`
        .hero {
          background: linear-gradient(-45deg, #c94b4b, #4b134f, #1e3a8a, #065f46);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
          padding: 100px 0;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }

        .hero::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -5%;
          width: 400px;
          height: 400px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
        }

        .hero > .container {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .hero h2 {
          font-size: 52px;
          color: white;
          margin-bottom: 16px;
          font-weight: 800;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .hero .subtitle {
          font-size: 24px;
          color: white;
          font-weight: 600;
          margin-bottom: 24px;
          text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
        }

        .hero .description {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.95);
          max-width: 800px;
          margin: 0 auto 32px;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-primary, .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 17px;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 600;
          transition: all 0.3s;
          cursor: pointer;
          border: none;
          font-size: 16px;
          font-family: 'Poppins', sans-serif;
        }

        .btn-primary {
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          color: white;
          box-shadow: 0 8px 24px rgba(255, 107, 53, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(255, 107, 53, 0.4);
        }

        .btn-secondary {
          background: white;
          color: #ff6b35;
          border: 2px solid #ff6b35;
          box-shadow: 0 4px 16px rgba(255, 107, 53, 0.1);
        }

        .btn-secondary:hover {
          background: #ff6b35;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 107, 53, 0.3);
        }

        @media (max-width: 768px) {
          .hero {
            padding-top: 60px;
          }

          .hero h2 {
            font-size: 26px;
          }

          .hero .subtitle {
            font-size: 18px;
          }

          .hero .description {
            font-size: 16px;
          }
        }
      `}</style>

      <section id="home" className="hero">
        <div className="container">
          <h2>{t.hero.title}</h2>
          <p className="subtitle">{t.hero.subtitle}</p>
          <p className="description">{t.hero.description}</p>
          <div className="cta-buttons">
            <button 
              onClick={(e) => handleScrollTo(e, 'services')}
              className="btn-primary"
            >
              {t.hero.cta}
              <ChevronRight size={20} />
            </button>
            <button 
              onClick={handleContactClick}
              className="btn-secondary" // ✅ Added back the styling class
            >
              {t.hero.cta2}
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;